import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@mui/material';
import { isMobile } from 'react-device-detect';

// Custom hooks
import { useMeetingContext } from '../context/MeetingContext';
import { useMainPageUI } from '../hooks/useMainPageUI';
import { useMobileSwipe } from '../hooks/useMobileSwipe';

// Layout components
import MobileLayout from './layouts/MobileLayout';
import DesktopLayout from './layouts/DesktopLayout';

// Components
import RightPanel from './RightPanel';
import { tutoMainPage } from './tutoMainPage.js';

// Utils
import { formatMeetingDate, needsDateFormatting } from '../utils/dateUtils';
import { getSentence } from './translations.js';
import { idKey, dateKey } from './MeetingsKeysList.js';

// Styles
import '../css/mainPageLeftPanel.css';
import '../css/globalVars.css';

const MainPage = () => {
  // Helper function to ensure consistent date processing
  const processMeetingForDisplay = useCallback((meeting) => {
    if (!meeting) return null;
    
    const needsFormatting = needsDateFormatting(meeting[dateKey]);
    if (needsFormatting) {
      const formatted = formatMeetingDate(meeting[dateKey]);
      return { ...meeting, [dateKey]: formatted };
    }
    return meeting;
  }, []);

  // Custom hooks
  const {
    meetings,
    loading,
    sortingLoading,
    loadMeetings,
    deleteMeeting,
    togglePin,
    updateMeeting,
    selectMeeting,
    clearSelectedMeeting,
    getMeetingData,
    currentSelectedMeeting
  } = useMeetingContext();

  const {
    sizes,
    rightPanelTabValue,
    pinnedAccordionExpanded,
    regularAccordionExpanded,
    updatePanelSizes,
    handleTabChange,
    handlePinnedAccordionChange,
    handleRegularAccordionChange
  } = useMainPageUI();

  // Local state - removed selectedMeeting, using context directly
  const [selectedMeetingId, setSelectedMeetingId] = useState(-1);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [pendingShareId, setPendingShareId] = useState(null);
  const [isTextSelecting, setIsTextSelecting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Mobile swipe handling
  const shouldDisableSwipe = isTextSelecting || isModalOpen || !!pendingDeleteId || !!pendingShareId;
  const {
    activePanelIndex,
    isAnimating,
    containerRef,
    switchToRightPanel,
    switchToLeftPanel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    setActivePanelIndex
  } = useMobileSwipe(currentSelectedMeeting, selectedMeetingId, (panelIndex) => {
    // Optional callback when panel switches
    console.log(`Switched to panel ${panelIndex}`);
  }, shouldDisableSwipe);

  // Auto-switch to left panel when no meeting is selected (mobile only)
  useEffect(() => {
    const hasNoMeeting = !currentSelectedMeeting && (!selectedMeetingId || selectedMeetingId === -1);
    if (hasNoMeeting && activePanelIndex === 1 && !isAnimating && isMobile) {
      switchToLeftPanel();
    }
  }, [currentSelectedMeeting, selectedMeetingId, activePanelIndex, isAnimating, switchToLeftPanel]);

  // Format dates for meetings that need it
  const processedMeetings = useMemo(() => {
    return meetings.map(meeting => {
      if (needsDateFormatting(meeting[dateKey])) {
        return {
          ...meeting,
          [dateKey]: formatMeetingDate(meeting[dateKey])
        };
      }
      return meeting;
    });
  }, [meetings]);

  // Initialize URL handling
  useEffect(() => {
    const handlePopState = () => {
      const urlParts = window.location.href.split('/');
      try {
        const tabValue = Number(urlParts[4]);
        const meetingId = Number(urlParts[5]);
        
        if (!isNaN(tabValue)) handleTabChange(tabValue);
        if (!isNaN(meetingId)) {
          setSelectedMeetingId(meetingId);
          
          // Load full meeting details from server (context handles the state)
          selectMeeting(meetingId).catch(error => {
            console.error('Error loading meeting details from URL:', error);
          });
          
          // For mobile, switch to right panel when navigating via URL
          if (isMobile) {
            setTimeout(() => {
              switchToRightPanel();
            }, 50);
          }
        } else if (isMobile) {
          // No meeting in URL, go back to meetings list
          setActivePanelIndex(0);
        }
      } catch (error) {
        console.error('Error parsing URL:', error);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [meetings, handleTabChange, processMeetingForDisplay, switchToRightPanel, setActivePanelIndex, selectMeeting]);

  // Load meetings on component mount
  useEffect(() => {
    loadMeetings('id', 1, true); // Mark as initial load
  }, [loadMeetings]);

  // Handle initial meeting selection from URL (only on first load and meetings change)
  useEffect(() => {
    const urlParts = window.location.href.split('/');
    const urlMeetingId = Number(urlParts.pop());
    
    // Only set from URL if we don't have a selected meeting yet, or if meetings just loaded
    if (!isNaN(urlMeetingId) && !currentSelectedMeeting && meetings.length > 0) {
      const meeting = getMeetingData(urlMeetingId);
      if (meeting) {
        setSelectedMeetingId(urlMeetingId);
        
        // Load full meeting details from server (context will handle the state)
        selectMeeting(urlMeetingId).catch(error => {
          console.error('Error loading meeting details from URL:', error);
        });
        
        // For mobile, switch to right panel when loading from URL
        if (isMobile) {
          setTimeout(() => {
            switchToRightPanel();
          }, 100);
        }
      }
    }
  }, [meetings, currentSelectedMeeting, switchToRightPanel, getMeetingData, selectMeeting]);

  // Handle desktop panel sizing
  useEffect(() => {
    if (!isMobile) {
      if (meetings.length === 0 && !loading) {
        // When no meetings, show tutorial in right panel and hide left panel
        updatePanelSizes(['0%', '100%']);
      } else if (meetings.length > 0) {
        // When meetings exist, show both panels
        updatePanelSizes(['30%', 'auto']);
      }
    }
  }, [meetings.length, loading, updatePanelSizes]);

  // Meeting selection handler - loads full meeting data from server
  const handleMeetingSelect = useCallback(async (meeting) => {
    const meetingId = meeting[idKey];
    
    // Always update the selected meeting ID first
    setSelectedMeetingId(meetingId);
    
    // Update URL - get current tab value at runtime to avoid dependency
    const currentTabValue = rightPanelTabValue;
    window.history.pushState(
      null, 
      null, 
      `/meeting/${currentTabValue}/${meetingId}`
    );

    // For mobile, automatically switch to right panel
    if (isMobile) {
      setTimeout(() => {
        switchToRightPanel();
      }, 50);
    }

    // Load full meeting details from server (context handles state)
    try {
      await selectMeeting(meetingId);
    } catch (error) {
      console.error('Error loading full meeting details:', error);
    }
  }, [selectMeeting, switchToRightPanel, rightPanelTabValue]);

  // Delete meeting handler
  const handleMeetingDelete = useCallback((meetingId) => {
    setPendingDeleteId(meetingId);
  }, []);

  // Toggle pin handler
  const handleMeetingTogglePin = useCallback(async (meetingId, currentPinStatus) => {
    try {
      await togglePin(meetingId, currentPinStatus);
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  }, [togglePin]);

  // Share meeting handler
  const handleMeetingShare = useCallback((meetingId) => {
    setPendingShareId(meetingId);
  }, []);

  // Tab change handler with URL update
  const handleRightPanelTabChange = useCallback(async (newValue, meeting) => {
    // If leaving editor tab, refresh meeting data
    if (rightPanelTabValue === 2 && currentSelectedMeeting) {
      try {
        await selectMeeting(currentSelectedMeeting[idKey]);
      } catch (error) {
        console.error('Error refreshing meeting:', error);
      }
    }

    handleTabChange(newValue);
    
    // Update URL
    if (currentSelectedMeeting) {
      window.history.pushState(
        null,
        null,
        `/meeting/${newValue}/${currentSelectedMeeting[idKey]}`
      );
    }
  }, [rightPanelTabValue, currentSelectedMeeting, handleTabChange, selectMeeting]);

  // Meeting update handler - context handles both list and selected meeting updates
  const handleMeetingUpdate = useCallback((meetingId, updates) => {
    updateMeeting(meetingId, updates);
  }, [updateMeeting]);

  // Render right panel content
  const renderRightPanel = () => {
    if (loading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          height: '100%',
          padding: '20px',
        }}>
          <Card 
            elevation={2}
            sx={{ 
              maxWidth: 400, 
              width: '100%',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <h3>{getSentence(126)}</h3>
              <p style={{ color: '#666', marginTop: '10px' }}>
                {getSentence(127)}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show tutorial when no selected meeting (regardless of whether meetings exist)
    if (!currentSelectedMeeting) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          height: '100%',
          padding: '20px',
        }}>
          <Card 
            elevation={3}
            sx={{ 
              maxWidth: 600, 
              width: '100%',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              {tutoMainPage()}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Process meeting for display consistency
    const displayMeeting = processMeetingForDisplay(currentSelectedMeeting);

    return (
      <RightPanel
        meeting={displayMeeting}
        tabValue={rightPanelTabValue}
        onTabChange={handleRightPanelTabChange}
        onMeetingUpdate={handleMeetingUpdate}
        onMeetingDelete={handleMeetingDelete}
        onMeetingShare={handleMeetingShare}
        onEditorSelectionChange={setIsTextSelecting}
        onModalStateChange={setIsModalOpen}
      />
    );
  };

  // Common dialog handlers
  const handleDeleteConfirm = useCallback(async () => {
    if (pendingDeleteId) {
      try {
        await deleteMeeting(pendingDeleteId);
        if (selectedMeetingId === pendingDeleteId) {
          setSelectedMeetingId(-1);
          clearSelectedMeeting();
        }
      } catch (error) {
        console.error('Error deleting meeting:', error);
      }
    }
    setPendingDeleteId(null);
  }, [pendingDeleteId, selectedMeetingId, deleteMeeting, clearSelectedMeeting]);

  const handleDeleteCancel = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const handleShareClose = useCallback(() => {
    setPendingShareId(null);
  }, []);

  // Render the appropriate layout based on device type
  if (isMobile) {
    return (
      <MobileLayout
        // Mobile swipe props
        containerRef={containerRef}
        activePanelIndex={activePanelIndex}
        isAnimating={isAnimating}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
        handleTouchCancel={handleTouchCancel}
        switchToLeftPanel={switchToLeftPanel}
        
        // Meeting data
        meetings={meetings}
        processedMeetings={processedMeetings}
        selectedMeetingId={selectedMeetingId}
        sortingLoading={sortingLoading}
        
        // Dialog state
        pendingDeleteId={pendingDeleteId}
        pendingShareId={pendingShareId}
        
        // Event handlers
        onMeetingSelect={handleMeetingSelect}
        onMeetingDelete={handleMeetingDelete}
        onMeetingTogglePin={handleMeetingTogglePin}
        onMeetingShare={handleMeetingShare}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteCancel={handleDeleteCancel}
        onShareClose={handleShareClose}
        onFileUploadSuccess={loadMeetings}
        loadMeetings={loadMeetings}
        
        // Accordion state
        pinnedAccordionExpanded={pinnedAccordionExpanded}
        regularAccordionExpanded={regularAccordionExpanded}
        onPinnedAccordionChange={handlePinnedAccordionChange}
        onRegularAccordionChange={handleRegularAccordionChange}
        
        // Right panel content
        renderRightPanel={renderRightPanel}
      />
    );
  }

  return (
    <DesktopLayout
      // Panel state
      sizes={sizes}
      updatePanelSizes={updatePanelSizes}
      
      // Meeting data
      meetings={meetings}
      processedMeetings={processedMeetings}
      selectedMeetingId={selectedMeetingId}
      sortingLoading={sortingLoading}
      
      // Dialog state
      pendingDeleteId={pendingDeleteId}
      pendingShareId={pendingShareId}
      
      // Event handlers
      onMeetingSelect={handleMeetingSelect}
      onMeetingDelete={handleMeetingDelete}
      onMeetingTogglePin={handleMeetingTogglePin}
      onMeetingShare={handleMeetingShare}
      onDeleteConfirm={handleDeleteConfirm}
      onDeleteCancel={handleDeleteCancel}
      onShareClose={handleShareClose}
      onFileUploadSuccess={loadMeetings}
      loadMeetings={loadMeetings}
      
      // Accordion state
      pinnedAccordionExpanded={pinnedAccordionExpanded}
      regularAccordionExpanded={regularAccordionExpanded}
      onPinnedAccordionChange={handlePinnedAccordionChange}
      onRegularAccordionChange={handleRegularAccordionChange}
      
      // Right panel content
      renderRightPanel={renderRightPanel}
    />
  );
};

export default MainPage; 